import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { EmailCopy } from "@/overlay/EmailCopy";

export function ContactSection() {
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px -10% 0px" });
  const isZh = i18n.language?.startsWith("zh");

  return (
    <section
      ref={ref}
      id="contact"
      data-testid="contact-section"
      className="relative w-full pt-24 sm:pt-32 pb-24"
    >
      <div className="relative z-10 w-full max-w-[1120px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-[1px] w-8 bg-[var(--gold-400)]/60" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-mono-brand text-[var(--paper-100)]/80">
            {t("contact.eyebrow")}
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.0, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className={[
            "max-w-[820px] leading-[1.1] tracking-[-0.02em]",
            "text-[var(--paper-50)] text-shadow-depth text-3xl sm:text-5xl",
            isZh ? "font-display-cn" : "font-display-en",
          ].join(" ")}
          data-testid="contact-title"
        >
          {t("contact.title")}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.25 }}
          className={[
            "mt-6 max-w-[640px] text-[var(--paper-100)]/80 text-base sm:text-lg leading-relaxed",
            isZh ? "font-body-cn" : "font-body-en",
          ].join(" ")}
        >
          {t("contact.description")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.0, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 sm:mt-20"
          data-testid="contact-email-block"
        >
          <EmailCopy email="info@poseidonmusic.net" />
        </motion.div>

        <motion.footer
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 1.2, delay: 0.7 }}
          className="mt-28 sm:mt-36 pt-8 border-t border-white/5 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase font-mono-brand text-[var(--paper-100)]/55"
        >
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} {t("footer.copy")}
          </span>
          <span className="hidden sm:inline">{t("footer.rights")}</span>
        </motion.footer>
      </div>
    </section>
  );
}
